import React, { useEffect, useState } from 'react';
import {
  Box, Card, CircularProgress, Grid, Stack, Tab, Tabs, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { branchProfitLoss, branchActionLogs } from '../../services/branchApi';
import BranchTxnReport from './BranchTxnReport';

const Stat = ({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) => (
  <Card sx={{ p: 2.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5" fontWeight={700} sx={{ color }}>{value}</Typography>
  </Card>
);

function Overview() {
  const [pl, setPl] = useState<any>(null);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: '', to: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [p, l] = await Promise.all([branchProfitLoss(range), branchActionLogs()]);
      setPl(p); setLogs(l.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.from, range.to]);

  if (loading && !pl) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mb: 2 }}>
        <TextField type="date" size="small" label="From" InputLabelProps={{ shrink: true }}
          value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
        <TextField type="date" size="small" label="To" InputLabelProps={{ shrink: true }}
          value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
      </Stack>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Profit / Loss</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}><Stat label="Deposits" value={`₹${(pl?.deposits ?? 0).toLocaleString()}`} color="#0ECC68" /></Grid>
        <Grid item xs={12} sm={4}><Stat label="Withdrawals" value={`₹${(pl?.withdrawals ?? 0).toLocaleString()}`} color="#E01B4F" /></Grid>
        <Grid item xs={12} sm={4}><Stat label="Net" value={`₹${(pl?.netProfit ?? 0).toLocaleString()}`} /></Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Activity Logs</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['When', 'Actor', 'Action', 'Target'].map((h) => (
              <TableCell key={h} sx={{ color: 'text.secondary' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {logs.map((l) => (
            <TableRow key={l.id} hover>
              <TableCell sx={{ fontSize: 12 }}>{new Date(l.created_at).toLocaleString()}</TableCell>
              <TableCell>{l.actor || '-'}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{l.action}</TableCell>
              <TableCell sx={{ fontSize: 12 }}>{l.target_type ? `${l.target_type}#${l.target_id}` : '-'}</TableCell>
            </TableRow>
          ))}
          {!logs.length && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No activity yet.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function BranchReports() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Reports</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Deposit Report" />
        <Tab label="Withdraw Report" />
        <Tab label="Overview & Activity" />
      </Tabs>
      {tab === 0 && <BranchTxnReport kind="deposits" />}
      {tab === 1 && <BranchTxnReport kind="withdrawals" />}
      {tab === 2 && <Overview />}
    </Box>
  );
}
